import { Component } from 'react';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, FragmentType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField, PatientContext } from 'models/ui';
import { NAV_ID } from 'constants/app.constant';
import { appHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { withChartLogic } from 'components/common';
import { FormField, FormFieldLabel, SectionTitle } from './constants';
import DischargeSummaryScreenView from './DischargeSummaryScreenView';
import withAdmissionHistory from '../shared/withAdmissionHistory';

interface DischargeSummaryScreenState {
  fragment: ChartFragment;
  statusFragment: ChartFragment;
  isLocked: boolean;
}

interface DischargeSummaryScreenProps extends ChartComponentProps {
  patientContext: PatientContext;
}

const getRecordContent = (fragment: ChartFragment, sectionTitle: string, formField: string) =>
  chartHelper.getFragmentRecords(fragment, sectionTitle, formField)?.[0]?.content || '';

class DischargeSummaryScreen extends Component<DischargeSummaryScreenProps, DischargeSummaryScreenState> {
  static displayName = 'DischargeSummaryScreen';

  constructor(props) {
    super(props);
    this.state = {
      fragment: null,
      statusFragment: null,
      isLocked: false
    };
  }

  componentDidMount() {
    return appHelper.useLoader(
      this.props.loadChartData().then(({ data: fragments }) => {
        const chartFragment = fragments.find((fragment) => fragment.fragmentType !== FragmentType.STATUS) ?? null;
        const statusFragment = chartHelper.findStatusFragment(fragments, NAV_ID.DISCHARGE_SUMMARY, NAV_ID.ADMISSION_HISTORY);

        this.setState(
          {
            fragment: chartFragment,
            statusFragment,
            isLocked: chartHelper.isChartLocked(fragments, this.props.selectedNavId)
          },
          () => {
            this.props.initState(this.buildDefaultFormFields());
          }
        );
      }),
      { errorMessage: 'can not load chart data' }
    );
  }

  buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
    const { fragment } = this.state;
    const { createFormField, getFragmentValue, getFragmentContentIds } = chartHelper;
    const dataMap = new Map<string, ChartMetaFormField>();

    const dateFields = [
      { name: FormField.ADMISSION_DATE, label: FormFieldLabel.ADMISSION_DATE, sectionTitle: SectionTitle.DISCHARGE_SUMMARY },
      { name: FormField.DISCHARGE_DATE, label: FormFieldLabel.DISCHARGE_DATE, sectionTitle: SectionTitle.DISCHARGE_SUMMARY },
      { name: FormField.CLINICIAN_SIGNATURE_DATE, label: FormFieldLabel.CLINICIAN_SIGNATURE_DATE, sectionTitle: SectionTitle.CLINICIAN_SIGNATURE }
    ];

    const textAreas = [
      { name: FormField.PRESENTING_PROBLEM, label: FormFieldLabel.PRESENTING_PROBLEM, sectionTitle: SectionTitle.REASON_FOR_ADMISSION },
      { name: FormField.PRINCIPAL_DIAGNOSIS, label: FormFieldLabel.PRINCIPAL_DIAGNOSIS, sectionTitle: SectionTitle.REASON_FOR_ADMISSION },
      { name: FormField.ADDITIONAL_DIAGNOSIS, label: FormFieldLabel.ADDITIONAL_DIAGNOSIS, sectionTitle: SectionTitle.REASON_FOR_ADMISSION },
      { name: FormField.SUMMARY_OF_HOSPITAL_STAY, label: FormFieldLabel.SUMMARY_OF_HOSPITAL_STAY, sectionTitle: SectionTitle.REASON_FOR_ADMISSION },

      { name: FormField.KEY_INVESTIGATIONS_AND_RESULTS, label: FormFieldLabel.KEY_INVESTIGATIONS_AND_RESULTS, sectionTitle: SectionTitle.CLINICAL_SUMMARY },
      { name: FormField.PROCEDURES_PERFORMED, label: FormFieldLabel.PROCEDURES_PERFORMED, sectionTitle: SectionTitle.CLINICAL_SUMMARY },
      { name: FormField.RESPONSE_TO_TREATMENT, label: FormFieldLabel.RESPONSE_TO_TREATMENT, sectionTitle: SectionTitle.CLINICAL_SUMMARY },
      { name: FormField.OUTSTANDING_ISSUES, label: FormFieldLabel.OUTSTANDING_ISSUES, sectionTitle: SectionTitle.CLINICAL_SUMMARY },

      { name: FormField.DISCHARGE_MEDICATIONS, label: FormFieldLabel.DISCHARGE_MEDICATIONS, sectionTitle: SectionTitle.MEDICATIONS },
      { name: FormField.CHANGES_FROM_ADMISSION_MEDICATIONS, label: FormFieldLabel.CHANGES_FROM_ADMISSION_MEDICATIONS, sectionTitle: SectionTitle.MEDICATIONS },
      { name: FormField.REASONS_FOR_CHANGES, label: FormFieldLabel.REASONS_FOR_CHANGES, sectionTitle: SectionTitle.MEDICATIONS },
      {
        name: FormField.ALLERGIES_AND_ADVERSE_DRUG_REACTIONS,
        label: FormFieldLabel.ALLERGIES_AND_ADVERSE_DRUG_REACTIONS,
        sectionTitle: SectionTitle.MEDICATIONS
      },

      { name: FormField.APPOINTMENTS, label: FormFieldLabel.APPOINTMENTS, sectionTitle: SectionTitle.FOLLOW_UP_AND_REFERRALS },
      { name: FormField.REFERRALS_TO_OTHER, label: FormFieldLabel.REFERRALS_TO_OTHER, sectionTitle: SectionTitle.FOLLOW_UP_AND_REFERRALS },

      {
        name: FormField.DISCHARGE_INSTRUCTIONS,
        label: FormFieldLabel.DISCHARGE_INSTRUCTIONS,
        sectionTitle: SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS
      },
      {
        name: FormField.WARNING_SIGNS_TO_WATCH_FOR,
        label: FormFieldLabel.WARNING_SIGNS_TO_WATCH_FOR,
        sectionTitle: SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS
      },
      {
        name: FormField.CONTACT_INFORMATION_FOR_URGENT_CONCERNS,
        label: FormFieldLabel.CONTACT_INFORMATION_FOR_URGENT_CONCERNS,
        sectionTitle: SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS
      },

      {
        name: FormField.HOME_CARE_SERVICES_ARRANGED,
        label: FormFieldLabel.HOME_CARE_SERVICES_ARRANGED,
        sectionTitle: SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT
      },
      {
        name: FormField.EQUIPMENT_PROVIDED,
        label: FormFieldLabel.EQUIPMENT_PROVIDED,
        sectionTitle: SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT
      },
      {
        name: FormField.TRANSPORT_ARRANGEMENTS,
        label: FormFieldLabel.TRANSPORT_ARRANGEMENTS,
        sectionTitle: SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT
      },

      {
        name: FormField.PATIENT_CARER_INVOLVEMENT_IN_DISCHARGE_PLANNING,
        label: FormFieldLabel.PATIENT_CARER_INVOLVEMENT_IN_DISCHARGE_PLANNING,
        sectionTitle: SectionTitle.CONSENT_AND_COMMUNICATION
      },
      {
        name: FormField.INTERPRETER_SERVICES_USED,
        label: FormFieldLabel.INTERPRETER_SERVICES_USED,
        sectionTitle: SectionTitle.CONSENT_AND_COMMUNICATION
      },
      {
        name: FormField.CONSENT_FOR_INFORMATION_SHARING,
        label: FormFieldLabel.CONSENT_FOR_INFORMATION_SHARING,
        sectionTitle: SectionTitle.CONSENT_AND_COMMUNICATION
      },

      {
        name: FormField.HANDOVER_TO_RECEIVING_HEALTHCARE_PROVIDER,
        label: FormFieldLabel.HANDOVER_TO_RECEIVING_HEALTHCARE_PROVIDER,
        sectionTitle: SectionTitle.CLINICAL_HANDOVER
      }
    ];

    const textBoxes = [
      { name: FormField.CLINICIAN_SIGNATURE_NAME, label: FormFieldLabel.CLINICIAN_SIGNATURE_NAME, sectionTitle: SectionTitle.CLINICIAN_SIGNATURE },
      { name: FormField.CLINICIAN_SIGNATURE_POSITION, label: FormFieldLabel.CLINICIAN_SIGNATURE_POSITION, sectionTitle: SectionTitle.CLINICIAN_SIGNATURE },
      { name: FormField.ATTENDING_CLINICIANS, label: FormFieldLabel.ATTENDING_CLINICIANS, sectionTitle: SectionTitle.TREATING_TEAM_AND_CONTACT_DETAILS },
      { name: FormField.WARD_UNIT, label: FormFieldLabel.WARD_UNIT, sectionTitle: SectionTitle.TREATING_TEAM_AND_CONTACT_DETAILS }
    ];

    dateFields.forEach(({ name, label, sectionTitle }) => {
      dataMap.set(
        name,
        createFormField({
          name,
          type: FormFieldInputType.DATE,
          label,
          value: getFragmentValue(fragment, sectionTitle, name)
        })
      );
    });

    textBoxes.forEach(({ name, label, sectionTitle }) => {
      dataMap.set(
        name,
        createFormField({
          name,
          type: FormFieldInputType.TEXT_BOX,
          label,
          value: getRecordContent(fragment, sectionTitle, name)
        })
      );
    });

    textAreas.forEach(({ name, label, sectionTitle }) => {
      dataMap.set(
        name,
        createFormField({
          name,
          type: FormFieldInputType.TEXT_AREA,
          label,
          value: getRecordContent(fragment, sectionTitle, name)
        })
      );
    });

    dataMap.set(
      FormField.REFERRALS_TO,
      createFormField({
        name: FormField.REFERRALS_TO,
        type: FormFieldInputType.CHECK_BOX,
        label: FormFieldLabel.REFERRALS_TO,
        contentIds: getFragmentContentIds(fragment, SectionTitle.FOLLOW_UP_AND_REFERRALS, FormField.REFERRALS_TO)
      })
    );

    return dataMap;
  };

  handleSaveClick = () => {
    this.props.handleSaveClick([this.buildFragment()], { afterSave: this.props.afterSave });
  };

  buildFragment = () => {
    const { buildPatientRecord, buildPatientRecords } = chartService;
    const { formFieldMap } = this.props;

    const record = {
      chartTitle: 'Admission History',
      fragmentTitle: SectionTitle.DISCHARGE_SUMMARY,
      records: [
        {
          sectionTitle: SectionTitle.DISCHARGE_SUMMARY,
          records: [buildPatientRecord(formFieldMap, FormField.ADMISSION_DATE), buildPatientRecord(formFieldMap, FormField.DISCHARGE_DATE)]
        },
        {
          sectionTitle: SectionTitle.REASON_FOR_ADMISSION,
          records: [
            buildPatientRecord(formFieldMap, FormField.PRESENTING_PROBLEM),
            buildPatientRecord(formFieldMap, FormField.PRINCIPAL_DIAGNOSIS),
            buildPatientRecord(formFieldMap, FormField.ADDITIONAL_DIAGNOSIS),
            buildPatientRecord(formFieldMap, FormField.SUMMARY_OF_HOSPITAL_STAY)
          ]
        },
        {
          sectionTitle: SectionTitle.TREATING_TEAM_AND_CONTACT_DETAILS,
          records: [buildPatientRecord(formFieldMap, FormField.ATTENDING_CLINICIANS), buildPatientRecord(formFieldMap, FormField.WARD_UNIT)]
        },
        {
          sectionTitle: SectionTitle.CLINICAL_SUMMARY,
          records: [
            buildPatientRecord(formFieldMap, FormField.KEY_INVESTIGATIONS_AND_RESULTS),
            buildPatientRecord(formFieldMap, FormField.PROCEDURES_PERFORMED),
            buildPatientRecord(formFieldMap, FormField.RESPONSE_TO_TREATMENT),
            buildPatientRecord(formFieldMap, FormField.OUTSTANDING_ISSUES)
          ]
        },
        {
          sectionTitle: SectionTitle.MEDICATIONS,
          records: [
            buildPatientRecord(formFieldMap, FormField.DISCHARGE_MEDICATIONS),
            buildPatientRecord(formFieldMap, FormField.CHANGES_FROM_ADMISSION_MEDICATIONS),
            buildPatientRecord(formFieldMap, FormField.REASONS_FOR_CHANGES),
            buildPatientRecord(formFieldMap, FormField.ALLERGIES_AND_ADVERSE_DRUG_REACTIONS)
          ]
        },
        {
          sectionTitle: SectionTitle.FOLLOW_UP_AND_REFERRALS,
          records: [
            buildPatientRecord(formFieldMap, FormField.APPOINTMENTS),
            ...buildPatientRecords(formFieldMap, FormField.REFERRALS_TO),
            buildPatientRecord(formFieldMap, FormField.REFERRALS_TO_OTHER),
            buildPatientRecord(formFieldMap, FormField.NOTES)
          ]
        },
        {
          sectionTitle: SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS,
          records: [
            buildPatientRecord(formFieldMap, FormField.DISCHARGE_INSTRUCTIONS),
            buildPatientRecord(formFieldMap, FormField.WARNING_SIGNS_TO_WATCH_FOR),
            buildPatientRecord(formFieldMap, FormField.CONTACT_INFORMATION_FOR_URGENT_CONCERNS)
          ]
        },
        {
          sectionTitle: SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT,
          records: [
            buildPatientRecord(formFieldMap, FormField.HOME_CARE_SERVICES_ARRANGED),
            buildPatientRecord(formFieldMap, FormField.EQUIPMENT_PROVIDED),
            buildPatientRecord(formFieldMap, FormField.TRANSPORT_ARRANGEMENTS)
          ]
        },
        {
          sectionTitle: SectionTitle.CONSENT_AND_COMMUNICATION,
          records: [
            buildPatientRecord(formFieldMap, FormField.PATIENT_CARER_INVOLVEMENT_IN_DISCHARGE_PLANNING),
            buildPatientRecord(formFieldMap, FormField.INTERPRETER_SERVICES_USED),
            buildPatientRecord(formFieldMap, FormField.CONSENT_FOR_INFORMATION_SHARING)
          ]
        },
        {
          sectionTitle: SectionTitle.CLINICAL_HANDOVER,
          records: [buildPatientRecord(formFieldMap, FormField.HANDOVER_TO_RECEIVING_HEALTHCARE_PROVIDER)]
        },
        {
          sectionTitle: SectionTitle.CLINICIAN_SIGNATURE,
          records: [
            buildPatientRecord(formFieldMap, FormField.CLINICIAN_SIGNATURE_NAME),
            buildPatientRecord(formFieldMap, FormField.CLINICIAN_SIGNATURE_POSITION),
            buildPatientRecord(formFieldMap, FormField.CLINICIAN_SIGNATURE_DATE)
          ]
        }
      ]
    };

    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const fragmentId = this.state.fragment?.fragmentId;
    const basicInfo = chartService.createBaseFragment({ fragmentId, chartingTime: this.props.chartingTime });
    return { ...basicInfo, chartData: cleanRecord };
  };

  handleCancelClick = () => {
    this.props.initState(this.buildDefaultFormFields());
    appHelper.scrollTop();
  };

  render() {
    const { isLocked, fragment, statusFragment } = this.state;
    const { formFieldMap, formSubmittedCount, handleDiscardClick, displayAuthoringData, enableDisplayRecordsButton, hasUnsavedChanges, patientContext } = this.props;

    const chartActionsProps: ChartActionsComponentProps = {
      isLocked,
      saveButtonText: 'Save and Continue',
      saveButtonHasIcon: true,
      cancelButtonText: 'Cancel',
      onSaveClick: this.handleSaveClick,
      onCancelClick: () => handleDiscardClick(this.handleCancelClick),
      onDisplayRecordsClick: displayAuthoringData,
      enableSaveButton: hasUnsavedChanges,
      enableDisplayRecordsButton
    };

    const viewProps = {
      isLocked,
      fragment,
      statusFragment,
      chartActionsProps,
      formSubmittedCount,
      formFieldMap,
      patientContext
    };

    return <DischargeSummaryScreenView {...viewProps} />;
  }
}

export { DischargeSummaryScreen as BaseDischargeSummaryScreen };
export default compose(withChartLogic, withAdmissionHistory)(DischargeSummaryScreen);
